import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Layers, Zap, Link2 } from "lucide-react";

const rise = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };

const scores = [
  ["Deliver results with positive impact", "4.56", 91.2],
  ["Adapt & innovate", "4.52", 90.4],
  ["Learn & develop", "4.49", 89.8],
  ["Analyse & plan", "4.34", 86.8],
  ["Connect & collaborate", "4.28", 85.6],
];

const perfects = ["Seeks innovative approaches", "Embraces new technology", "Data-guided decisions", "Dynamic and driven", "Drives improvements"];

const testimonials = [
  ["Paulyn demonstrated a tremendous drive for results and a great ability to think on her feet. Her pioneering work on Virtual Reality and the SDG Learncast podcast placed UNSSC at the heart of the inter-agency SDG:Learn programme. She is THE colleague in KCSD who is always ready to try new things; always available to engage; and always helpful to all colleagues.", "First Reporting Officer", "UNSSC KCSD · Annual Appraisal · Frequently exceeds expectations"],
  ["She is always at the forefront of experimenting with new technologies, guiding colleagues on new tools to streamline workflows. She champions innovative approaches to work without losing focus on the strategic priorities of the organization and the system.", "External stakeholders", "360° Leadership Pathway Assessment · 2025 · Multiple raters"],
  ["Paulyn is particularly strong at keeping up with the latest trends and ideas, and she applies this learning to enhance the impact of her work. She excels at working at the intersection of programme, communications, and knowledge management, with a keen ability to see and leverage the connections across these disciplines.", "360° raters", "Leadership Pathway Assessment · 2025 · What she is particularly good at"],
];

const career = [
  ["2026–now", "Knowledge Management and Learning Innovation Officer", "UNSSC · Bonn, Germany", "Supervises two direct reports"],
  ["2020–2026", "Knowledge Management, Communications and Reporting Officer", "UNSSC · Bonn, Germany", "Supervised one direct report"],
  ["2016–2020", "Associate Fellow to the Head of Office", "UNSSC · Bonn, Germany"],
  ["2014–2016", "Associate Collaborator & Consultant", "UNSSC · Turin, Italy · First UNSSC role"],
  ["2013–2016", "Master of Public Policy · DAAD Helmut Schmidt Scholar", "Hertie School of Governance · Berlin"],
  ["2009–2013", "Lawyer & Supervising Political Affairs Officer", "Philippine House of Representatives", "Mobilized 100+ volunteers for Typhoon Ondoy relief · Supervised 20+ district office staff"],
  ["2006–2009", "Risk Management Analyst · HR Officer", "JP Morgan Chase · Energy Development Corp."],
];

const stats = [
  ["1.1", "M+", "SDG:Learn cumulative users"],
  ["100", "K+", "Podcast plays · Top 15% Spotify"],
  ["400", "K", "USD secured · Italian government (ASDA)"],
  ["350", "K", "USD multi-year BMZ portfolio managed"],
  ["50", "K+", "Registered learners (2026)"],
  ["59", "K", "LinkedIn followers"],
  ["20", "+", "African countries reached"],
];

export default function AboutTab() {
  const barsRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      barsRef.current?.querySelectorAll(".bar-fill").forEach((bar) => {
        bar.style.width = bar.dataset.width + "%";
      });
    }, 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="tab-panel" data-testid="tab-panel-about">
      <div className="panel-wrap white">
        <div className="eyebrow">About</div>
        <h2 className="heading">The person<br />behind the work.</h2>

        <div className="about-top">
          <motion.div variants={rise} initial="hidden" animate="show">
            <div className="overview-bio">
              <p>Paulyn works at an intersection few people occupy — between programme design and institutional architecture, between knowledge management and digital innovation, between delivery and the conditions that make delivery possible.</p>
              <p>Her work at UNSSC — where she has been since 2014 — spans the full ecosystem: securing donor funding, coordinating 68 UN partners, designing VR learning experiences, launching a top 15% Spotify podcast, and building the internal knowledge systems that help an institution learn from its own work.</p>
              <p>Languages: Filipino and English (native), German, Italian.</p>
            </div>
            <div className="overview-quote" style={{ marginTop: "1.5rem" }}>
              <p>"The real work is not delivering the programme. It is building the conditions, the learning systems, the institutional trust, the digital infrastructure, that make impact possible long after the programme ends."</p>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.8, color: "rgba(255,255,255,.72)", fontStyle: "normal", marginTop: ".9rem" }}>That is the ambition, not a finished achievement. I am still learning how to do it well, and clear that no one builds these conditions alone. Co-creation is how the work actually moves.</p>
              <cite>— Paulyn Duman</cite>
            </div>
            <div className="pillars" style={{ marginTop: "1.5rem" }}>
              <div className="pillar"><div className="pillar-svg-icon"><Layers className="icon" /></div><div><div className="pillar-title">Institutional learning architecture</div><div className="pillar-text">After Action Reviews, Chats with Experts, and living workflows and materials shared with colleagues. Built to help organizations learn from their own work.</div></div></div>
              <div className="pillar"><div className="pillar-svg-icon"><Zap className="icon" /></div><div><div className="pillar-title">Digital readiness and innovation infrastructure</div><div className="pillar-text">AI workshops, VR, workflow design and tool adoption. Led the UNSSC migration from Universal Analytics to GA4 and built a Looker Studio dashboard aligned to the UNSSC Strategic Framework, giving senior leaders direct visibility on performance.</div></div></div>
              <div className="pillar"><div className="pillar-svg-icon"><Link2 className="icon" /></div><div><div className="pillar-title">Resource stewardship and ecosystem positioning</div><div className="pillar-text">Multi-year accountability for a BMZ-funded portfolio spanning UN SDG:Learn multi-stakeholder engagement, VR, the UN Mastermind Group and moderated courses on policy coherence for sustainable development and the Paris Agreement, alongside Italian government funding for ASDA. Donor reporting, inter-agency intelligence and the frameworks that sustain continued operation.</div></div></div>
            </div>
          </motion.div>

          <motion.div variants={rise} initial="hidden" animate="show" transition={{ delay: 0.1 }} ref={barsRef}>
            <div className="stats-block">
              {stats.map(([n, s, l], i) => (
                <div className="stat-card" key={i}><div className="sc-num">{n}<span>{s}</span></div><div className="sc-lbl">{l}</div></div>
              ))}
            </div>
            <div className="mini-label" style={{ marginBottom: ".9rem" }}>UNSSC Leadership Pathway 360° · 13 raters · 2025</div>
            {scores.map(([label, val, w], i) => (
              <div className="score-row" key={i}>
                <div className="score-label">{label} <span>{val}</span></div>
                <div className="bar-track"><div className="bar-fill" data-width={w}></div></div>
              </div>
            ))}
            <div className="mini-label" style={{ margin: "1.2rem 0 .8rem" }}>Perfect 5.0/5.0 — all rater groups</div>
            <div className="perfect-row">
              {perfects.map((p, i) => (
                <div className="pb" key={i}><div className="pb-score">5.0</div><div className="pb-lbl">{p}</div></div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="about-bottom">
          <div>
            <div className="mini-label">What supervisors and colleagues say</div>
            {testimonials.map(([text, author, role], i) => (
              <motion.div className="tcard" key={i} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}>
                <div className="tmark">"</div>
                <p className="ttext">{text}</p>
                <div className="tauthor">{author}</div>
                <div className="trole2">{role}</div>
                <div className="stars">{[...Array(5)].map((_, j) => <div className="star" key={j}></div>)}</div>
              </motion.div>
            ))}
          </div>

          <div>
            <div className="mini-label">Career arc</div>
            <div className="timeline-compact">
              {career.map(([year, role, org, note], i) => (
                <div className="tc-item" key={i}>
                  <div className="tc-year">{year}</div>
                  <div className="tc-body"><div className="tc-role">{role}</div><div className="tc-org">{org}</div>{note && <div className="tc-note" data-testid={`career-note-${i}`}>{note}</div>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
